import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Coins,
  FileText,
  ListTodo,
  ListVideo,
  Loader2,
  MessageSquare,
  Sparkles,
} from 'lucide-react'

import {
  getProduction,
  getProductionOverview,
  listActivity,
  type ActivityItem,
  type OverviewTodo,
  type Production,
  type ProductionOverview,
} from '@/api/studio-production'
import { CatalogListPageShell } from '@/shared/components/catalog/CatalogListPageShell'
import {
  PRODUCTION_HERO_CLASS,
  PRODUCTION_INSET_PANEL_CLASS,
  PRODUCTION_QUICK_LINK_CLASS,
} from '@/features/studio/lib/production-shell'
import { cn } from '@/shared/lib/utils'
import { productionBasePath } from '@/features/studio/lib/production-path'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import {
  ProtoAvatar,
  ProtoBadge,
  ProtoCard,
  ProtoPagination,
  ProtoProgress,
  ProtoSectionTitle,
  ProtoStat,
} from '@/features/studio/components/ui-kit'

const ACTIVITY_PAGE_SIZE = 10

const QUICK_LINKS = [
  { labelKey: 'production.nav.script', icon: FileText, path: 'script' },
  { labelKey: 'production.nav.bible', icon: BookOpen, path: 'bible' },
  { labelKey: 'production.nav.shots', icon: ListVideo, path: 'shots' },
  { labelKey: 'production.nav.canvas', icon: Sparkles, path: 'canvas-boards' },
  { labelKey: 'production.nav.reviews', icon: MessageSquare, path: 'reviews' },
] as const

function memberLabel(name?: string, username?: string, userId?: number): string {
  const base = (name || username || (userId ? `#${userId}` : '?')).trim()
  if (base.length <= 2) return base.toUpperCase()
  return base.slice(0, 2).toUpperCase()
}

function todoIcon(type: OverviewTodo['type']) {
  switch (type) {
    case 'task':
      return <ListTodo className="h-4 w-4 text-sky-400" />
    case 'review':
      return <CheckCircle2 className="h-4 w-4 text-amber-400" />
    case 'bounty':
      return <Coins className="h-4 w-4 text-violet-400" />
    default:
      return <MessageSquare className="h-4 w-4 text-rose-400" />
  }
}

export function ProductionOverviewPage() {
  const { productionId = '' } = useParams()
  const { t } = useTranslation()
  const base = productionBasePath(productionId)
  const [row, setRow] = useState<Production | null>(null)
  const [overview, setOverview] = useState<ProductionOverview | null>(null)
  const [activity, setActivity] = useState<ActivityItem[]>([])
  const [activityTotal, setActivityTotal] = useState(0)
  const [activityPage, setActivityPage] = useState(0)
  const [activityLoading, setActivityLoading] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setActivityPage(0)
    void (async () => {
      setLoading(true)
      try {
        const [data, ov] = await Promise.all([
          getProduction(productionId),
          getProductionOverview(productionId),
        ])
        if (!cancelled) {
          setRow(data)
          setOverview(ov)
        }
      } catch (e) {
        if (!cancelled) setError(resolveApiErrorMessage(e, 'production.loadFailed'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [productionId])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setActivityLoading(true)
      try {
        const act = await listActivity(productionId, {
          limit: ACTIVITY_PAGE_SIZE,
          offset: activityPage * ACTIVITY_PAGE_SIZE,
        })
        if (!cancelled) {
          setActivity(act.items)
          setActivityTotal(act.total)
        }
      } catch {
        if (!cancelled) {
          setActivity([])
          setActivityTotal(0)
        }
      } finally {
        if (!cancelled) setActivityLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [productionId, activityPage])

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    )
  }

  if (error && !row) {
    return (
      <CatalogListPageShell>
        <p className="text-sm text-rose-300">{error ?? t('production.loadFailed')}</p>
      </CatalogListPageShell>
    )
  }

  if (!row || !overview) {
    return (
      <CatalogListPageShell>
        <p className="text-sm text-rose-300">{t('production.loadFailed')}</p>
      </CatalogListPageShell>
    )
  }

  const memberCount = row.member_count ?? (overview.member_preview.length || 1)
  const progress = overview.progress.filter((ep) => ep.total > 0 || ep.episode_no <= row.episode_target)

  return (
    <CatalogListPageShell>
      <div className={PRODUCTION_HERO_CLASS}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-50">{row.title}</h1>
            <p className="mt-2 max-w-xl text-sm text-zinc-400">
              {row.description || t('production.noDescription')}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {row.project_type ? (
                <ProtoBadge tone="indigo">
                  {t(`production.projectType.${row.project_type}`, { defaultValue: row.project_type })}
                </ProtoBadge>
              ) : null}
              <ProtoBadge tone="default">{row.aspect_ratio}</ProtoBadge>
              <ProtoBadge tone="violet">
                {t('overview.episodeTarget', { n: row.episode_target })}
              </ProtoBadge>
              {row.default_locale ? (
                <ProtoBadge tone="sky">{row.default_locale}</ProtoBadge>
              ) : null}
            </div>
          </div>
          <Link
            to={`${base}/team`}
            className={cn(PRODUCTION_INSET_PANEL_CLASS, 'flex items-center gap-2 px-3 py-2')}
          >
            <div className="flex -space-x-2">
              {overview.member_preview.length > 0 ? (
                overview.member_preview.map((m) => (
                  <ProtoAvatar
                    key={m.user_id}
                    label={memberLabel(m.display_name, m.username, m.user_id)}
                    size="sm"
                  />
                ))
              ) : (
                <ProtoAvatar label={t('production.nav.team').slice(0, 2)} size="sm" />
              )}
            </div>
            <span className="text-xs text-zinc-400">{t('overview.memberCount', { n: memberCount })}</span>
            <ArrowRight className="h-3.5 w-3.5 text-zinc-500" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ProtoCard production>
          <ProtoSectionTitle>{t('overview.todosTitle')}</ProtoSectionTitle>
          {overview.todos.length === 0 ? (
            <p className="text-xs text-zinc-500">{t('overview.todosEmpty')}</p>
          ) : (
            <ul className="space-y-2">
              {overview.todos.map((todo, idx) => (
                <li key={`${todo.type}-${todo.ref_id ?? idx}`}>
                  <Link
                    to={`${base}/${todo.link_path}`}
                    className={cn(PRODUCTION_INSET_PANEL_CLASS, 'flex items-center justify-between gap-3 px-3 py-2.5')}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2.5">
                      {todoIcon(todo.type)}
                      <span className="truncate text-sm text-zinc-200">{todo.title}</span>
                    </div>
                    {todo.hint ? (
                      <ProtoBadge tone="default" className="shrink-0 text-[10px]">
                        {todo.hint}
                      </ProtoBadge>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </ProtoCard>

        <ProtoCard production>
          <ProtoSectionTitle>{t('overview.progressTitle')}</ProtoSectionTitle>
          {progress.length === 0 || progress.every((ep) => ep.total === 0) ? (
            <p className="text-xs text-zinc-500">{t('overview.progressEmpty')}</p>
          ) : (
            <div className="space-y-4">
              {progress.map((ep) =>
                ep.total > 0 ? (
                  <div key={ep.episode_no}>
                    <ProtoProgress
                      value={ep.approved}
                      max={ep.total || 1}
                      label={t('overview.episodeApproved', {
                        ep: ep.episode_label,
                        approved: ep.approved,
                        total: ep.total,
                      })}
                    />
                    <div className="mt-1 flex gap-3 text-[10px] text-zinc-600">
                      <span>
                        {t('overview.storyboarded', { n: ep.storyboarded, total: ep.total })}
                      </span>
                      <span>{t('overview.withTake', { n: ep.with_take, total: ep.total })}</span>
                    </div>
                  </div>
                ) : null,
              )}
            </div>
          )}
        </ProtoCard>
      </div>

      <ProtoCard className="mt-4" production>
        <ProtoSectionTitle>{t('overview.quickLinks')}</ProtoSectionTitle>
        <div className="flex flex-wrap gap-2">
          {QUICK_LINKS.map((q) => (
            <Link
              key={q.path}
              to={`${base}/${q.path}`}
              className={PRODUCTION_QUICK_LINK_CLASS}
            >
              <q.icon className="h-4 w-4" />
              {t(q.labelKey)}
            </Link>
          ))}
        </div>
      </ProtoCard>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <ProtoStat production label={t('production.stats.aspect')} value={row.aspect_ratio} />
        <ProtoStat production label={t('production.stats.episodes')} value={String(row.episode_target)} />
        <ProtoStat
          production
          label={t('production.stats.role')}
          value={t(`production.roles.${row.role ?? 'contributor'}`, { defaultValue: row.role ?? '—' })}
        />
        <ProtoStat production label={t('overview.shotCount')} value={String(overview.shot_count)} />
      </div>

      <ProtoCard className="mt-4" production>
        <ProtoSectionTitle>{t('production.activityTitle')}</ProtoSectionTitle>
        {activityLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-400" />
          </div>
        ) : activity.length === 0 ? (
          <p className="text-xs text-zinc-500">{t('production.activityEmpty')}</p>
        ) : (
          <>
            <ul className="space-y-2">
              {activity.map((a) => (
                <li key={a.id} className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-zinc-300">{a.summary || a.action}</span>
                  <time className="shrink-0 text-[11px] text-zinc-600">
                    {new Date(a.created_at).toLocaleString()}
                  </time>
                </li>
              ))}
            </ul>
            <ProtoPagination
              page={activityPage}
              pageSize={ACTIVITY_PAGE_SIZE}
              total={activityTotal}
              onPageChange={setActivityPage}
              prevLabel={t('pagination.prev')}
              nextLabel={t('pagination.next')}
              className="mt-4"
            />
          </>
        )}
      </ProtoCard>
    </CatalogListPageShell>
  )
}
